// The work register in the Census tab (docs/projects/work-register.md): the
// tenant's register builds newest first, a trigger for a new one, the org
// register a completed build wrote, and Serve, which makes that register the
// tenant review profile the judge reviews against. A build runs out of band on
// a GPU node of its own; the list polls while one is queued or running.
import { useEffect, useState } from "react";
import { getRegisterBuild, listRegisterBuilds, triggerRegisterBuild } from "../lib/censusClient.js";
import { getReviewProfile, serveRegisterBuild } from "../lib/reviewClient.js";
import { NOTE_CLASS } from "../lib/ui.js";

const ACTIVE = new Set(["queued", "running"]);
const POLL_MS = 15_000;

function fmtDate(ts) {
    if (!ts) return "";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function fmtDay(ts) {
    if (!ts) return "";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function message(err) {
    return err?.serverMessage || err?.message || String(err);
}

// A completed build's documents are deleted 90 days after it completes; its summary row stays. Past that date the
// build has no register to read or serve.
export function documentsExpired(build, now = Date.now()) {
    if (build.status !== "completed" || !build.documentsExpireAt) return false;
    const expiry = new Date(build.documentsExpireAt).getTime();
    return !Number.isNaN(expiry) && expiry <= now;
}

export function buildNote(build, now = Date.now()) {
    if (build.status === "completed") {
        return [
            `${build.norms} norms`,
            `${build.intents} intents`,
            `${Number(build.windowsRead || 0).toLocaleString()} windows`,
            fmtDate(build.completedAt),
            build.orgTokensSource === "none" ? "unmasked" : null,
            documentsExpired(build, now) ? "documents deleted" : null,
        ].filter(Boolean).join(" · ");
    }
    if (build.status === "failed") return `failed · ${String(build.errorMessage || "").slice(0, 120)}`;
    return build.status;
}

function orgProfile(documents) {
    return documents.find((d) => d.kind === "profile" && d.node === "org")?.document || null;
}

function RegisterRow({ build, open, onToggle }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className={[
                "grid w-full gap-[3px] border-0 border-t border-[color:var(--line)] bg-transparent px-[10px] py-2 text-left cursor-pointer",
                "hover:bg-[color:var(--control-bg)]",
                build.status === "failed" ? "opacity-60" : "",
            ].join(" ")}
        >
            <strong className="truncate text-[12px] leading-[1.12] font-medium">
                {`Register · ${String(build.buildId).slice(0, 8)}…`}
            </strong>
            <div className={`${NOTE_CLASS} truncate`}>{buildNote(build)}</div>
        </button>
    );
}

function RegisterDetail({ build, detail, canManage, serving, onServe }) {
    if (build.status !== "completed") {
        const note = build.status === "failed"
            ? "This build failed before it wrote a register."
            : "The register appears here when the build completes.";
        return <div className={`${NOTE_CLASS} px-[10px] pb-2`}>{note}</div>;
    }
    if (documentsExpired(build)) {
        return (
            <div className={`${NOTE_CLASS} px-[10px] pb-2`} data-register-expired="">
                {`This build's documents were deleted on ${fmtDay(build.documentsExpireAt)}, 90 days after it completed. `
                    + "Its summary stays; a register served from it is still in the review profile. "
                    + "Build the register again to read or serve a new one."}
            </div>
        );
    }
    if (!detail) return <div className={`${NOTE_CLASS} px-[10px] pb-2`}>Loading the register…</div>;
    if (detail.error) return <div className="px-[10px] pb-2 text-[11px] text-red-400">{detail.error}</div>;
    const profile = orgProfile(detail.documents);
    if (!profile) return <div className={`${NOTE_CLASS} px-[10px] pb-2`}>This build wrote no org register.</div>;
    return (
        <div className="grid gap-[6px] px-[10px] pb-2" data-register-detail="">
            <div className={NOTE_CLASS}>
                {`${detail.documents.filter((d) => d.kind === "profile").length} registers built; the org's is what Serve sends.`}
                {build.documentsExpireAt ? ` Kept until ${fmtDay(build.documentsExpireAt)}.` : ""}
            </div>
            <ol className="m-0 grid list-decimal gap-[4px] pl-[18px] text-[11px] leading-[1.3]">
                {(profile.norms || []).map((n) => <li key={n.ref || n.statement}>{n.statement}</li>)}
            </ol>
            {(profile.intents || []).length > 0 ? (
                <>
                    <div className={NOTE_CLASS}>Intents</div>
                    <ol className="m-0 grid list-decimal gap-[4px] pl-[18px] text-[11px] leading-[1.3]">
                        {profile.intents.map((i) => <li key={i.ref || i.statement}>{i.statement}</li>)}
                    </ol>
                </>
            ) : null}
            {canManage && build.status === "completed" ? (
                <button
                    type="button"
                    disabled={serving}
                    onClick={onServe}
                    className="justify-self-start rounded border border-[color:var(--line)] bg-transparent px-2 py-1 text-[12px] font-medium text-[color:var(--ink)] cursor-pointer hover:bg-[color:var(--control-bg)]"
                >
                    {serving ? "Serving…" : "Serve as the review profile"}
                </button>
            ) : null}
        </div>
    );
}

export default function RegisterBuildsPanel({ canManage }) {
    const [state, setState] = useState({ status: "loading", builds: [] });
    const [tick, setTick] = useState(0);
    const [notice, setNotice] = useState(null);
    const [problem, setProblem] = useState(null);
    const [starting, setStarting] = useState(false);
    const [open, setOpen] = useState(null);
    const [details, setDetails] = useState({});
    const [serving, setServing] = useState(null);

    useEffect(() => {
        let alive = true;
        listRegisterBuilds({ pageSize: 6 })
            .then((res) => { if (alive) setState({ status: "ready", builds: res.builds }); })
            .catch((err) => { if (alive) setState({ status: "error", builds: [], message: message(err) }); });
        return () => { alive = false; };
    }, [tick]);

    const active = state.builds.some((b) => ACTIVE.has(b.status));
    useEffect(() => {
        if (!active) return undefined;
        const t = setTimeout(() => setTick((n) => n + 1), POLL_MS);
        return () => clearTimeout(t);
    }, [active, tick]);

    const start = async () => {
        setStarting(true);
        setNotice(null);
        setProblem(null);
        try {
            await triggerRegisterBuild({});
            setNotice("Register build queued. It runs on its own GPU node and takes one to three hours.");
            setTick((n) => n + 1);
        } catch (err) {
            setProblem(message(err));
        } finally {
            setStarting(false);
        }
    };

    const toggle = (build) => setOpen((o) => (o === build.buildId ? null : build.buildId));

    // A build writes its documents when it completes and never changes them after, so the open row fetches them
    // once it is completed, including when a poll finds that the build it shows has just completed. A fetch that
    // failed is kept only to show its error: the next time the row opens it is tried again.
    const openBuild = state.builds.find((b) => b.buildId === open);
    const fetched = openBuild ? details[openBuild.buildId] : null;
    const fetchId = openBuild?.status === "completed" && !documentsExpired(openBuild) && (!fetched || fetched.error)
        ? openBuild.buildId
        : null;
    useEffect(() => {
        if (!fetchId) return undefined;
        let alive = true;
        getRegisterBuild(fetchId)
            .then((res) => { if (alive) setDetails((d) => ({ ...d, [fetchId]: { documents: res.documents } })); })
            .catch((err) => {
                if (alive) setDetails((d) => ({ ...d, [fetchId]: { documents: [], error: message(err) } }));
            });
        return () => { alive = false; };
    }, [fetchId]);

    const serve = async (build) => {
        setServing(build.buildId);
        setNotice(null);
        setProblem(null);
        try {
            const current = await getReviewProfile();
            const profile = await serveRegisterBuild({ buildId: build.buildId, expectedVersion: current.version });
            setNotice(`Serving as the review profile, version ${profile.version}. Reviews created from now on use it.`);
        } catch (err) {
            setProblem(message(err));
        } finally {
            setServing(null);
        }
    };

    return (
        <div data-register-builds="">
            <div className="grid gap-[2px] border-t border-[color:var(--line)] px-[10px] py-2">
                <strong className="text-[12px] leading-[1.12] font-medium">Work register</strong>
                <span className={NOTE_CLASS}>
                    The observed norms and inferred intents the review judge reads against, built from the newest
                    census reading. Build one, read it, then serve it.
                </span>
            </div>
            {notice ? (
                <div className="border-t border-[color:var(--line)] px-[10px] py-2">
                    <span className="text-[11px] text-[color:var(--green)]">{notice}</span>
                </div>
            ) : null}
            {problem ? (
                <div className="border-t border-[color:var(--line)] px-[10px] py-2">
                    <span className="text-[11px] text-red-400">{problem}</span>
                </div>
            ) : null}
            {canManage ? (
                <button
                    type="button"
                    disabled={starting || active}
                    onClick={start}
                    className="w-full border-0 border-t border-[color:var(--line)] bg-transparent px-[10px] py-2 text-left text-[12px] font-medium text-[color:var(--muted)] transition-colors hover:bg-[color:var(--control-bg)] hover:text-[color:var(--ink)] cursor-pointer disabled:cursor-default disabled:opacity-60"
                >
                    {starting ? "Queuing…" : active ? "A build is running…" : "+ Build register"}
                </button>
            ) : null}
            {state.status === "error" ? (
                <div className="border-t border-[color:var(--line)] px-[10px] py-2">
                    <span className="text-[11px] text-red-400">{state.message}</span>
                    <div className={NOTE_CLASS}>Register builds are not available on this server.</div>
                </div>
            ) : state.status === "loading" ? (
                <div className={`${NOTE_CLASS} border-t border-[color:var(--line)] px-[10px] py-2`}>Loading builds…</div>
            ) : (
                <>
                    {state.builds.map((b) => (
                        <div key={b.buildId}>
                            <RegisterRow build={b} open={open === b.buildId} onToggle={() => toggle(b)} />
                            {open === b.buildId ? (
                                <RegisterDetail
                                    build={b}
                                    detail={details[b.buildId]}
                                    canManage={canManage}
                                    serving={serving === b.buildId}
                                    onServe={() => serve(b)}
                                />
                            ) : null}
                        </div>
                    ))}
                    {state.builds.length === 0 ? (
                        <div className={`${NOTE_CLASS} border-t border-[color:var(--line)] px-[10px] py-2`}>
                            No register built for this tenant yet.
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}
